import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CreditCard, ShieldCheck, Zap, PlugZap, CheckCircle2, XCircle,
  AlertTriangle, PlayCircle, Save, ExternalLink, Info, Building2, Globe, Key,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const BUSINESS_TYPES = [
  { v: "proprietorship", l: "Proprietorship" },
  { v: "partnership", l: "Partnership" },
  { v: "llp", l: "LLP" },
  { v: "private_limited", l: "Private Limited" },
  { v: "public_limited", l: "Public Limited" },
  { v: "other", l: "Other" },
];

const emptyBusiness = {
  legal_name: "", brand_name: "", business_type: "", pan: "", gstin: "",
  email: "", phone: "", address: "",
};
const emptyWebsite = { website_url: "", support_email: "", support_phone: "" };

function Field({ label, hint, optional, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="mb-1.5 flex items-center gap-2">
        <span className="font-mono text-[11px] uppercase tracking-overline text-black/60">{label}</span>
        {optional && <span className="rounded-sm bg-black/5 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-black/40">Optional</span>}
      </div>
      {children}
      {hint && <p className="mt-1.5 text-[11px] leading-relaxed text-black/45">{hint}</p>}
    </label>
  );
}

const inputCls =
  "w-full rounded-md border border-black/15 bg-white px-3 py-2.5 text-sm text-black placeholder:text-black/30 focus:border-[#2F80ED] focus:outline-none";

function StatusPill({ ok, label, testid }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-overline ${
        ok ? "bg-emerald-500/10 text-emerald-700" : "bg-black/5 text-black/50"
      }`}
      data-testid={testid}
    >
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {label}
    </span>
  );
}

export default function AdminRazorpaySettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecrets, setShowSecrets] = useState({ test: false, live: false, webhook: false });

  const [business, setBusiness] = useState(emptyBusiness);
  const [website, setWebsite] = useState(emptyWebsite);
  const [mode, setMode] = useState("test");
  const [keys, setKeys] = useState({ test_key_id: "", test_key_secret: "", live_key_id: "", live_key_secret: "", webhook_secret: "" });

  const load = async () => {
    const { data } = await api.get("/admin/payments/razorpay-config");
    setCfg(data);
    setMode(data.mode || "test");
    setBusiness({ ...emptyBusiness, ...(data.business_info || {}) });
    setWebsite({ ...emptyWebsite, ...(data.website_info || {}) });
    setKeys({
      test_key_id: data.test_key_id || "",
      test_key_secret: "",
      live_key_id: data.live_key_id || "",
      live_key_secret: "",
      webhook_secret: "",
    });
  };

  useEffect(() => { load(); }, []);

  const patchConfig = async (payload, successMsg = "Saved") => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/payments/razorpay-config", payload);
      setCfg(data);
      toast.success(successMsg);
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveBusiness = () => patchConfig({ business_info: business }, "Business info saved");
  const saveWebsite = () => patchConfig({ website_info: website }, "Website info saved");

  const saveKeys = async () => {
    const payload = {};
    if (keys.test_key_id !== (cfg?.test_key_id || "")) payload.test_key_id = keys.test_key_id;
    if (keys.test_key_secret) payload.test_key_secret = keys.test_key_secret;
    if (keys.live_key_id !== (cfg?.live_key_id || "")) payload.live_key_id = keys.live_key_id;
    if (keys.live_key_secret) payload.live_key_secret = keys.live_key_secret;
    if (keys.webhook_secret) payload.webhook_secret = keys.webhook_secret;
    if (Object.keys(payload).length === 0) {
      toast.info("Nothing to save");
      return;
    }
    const data = await patchConfig(payload, "API keys saved securely");
    if (data) {
      // Clear entered secrets from UI (they're safely on server)
      setKeys((k) => ({ ...k, test_key_secret: "", live_key_secret: "", webhook_secret: "" }));
    }
  };

  const changeMode = async (nextMode) => {
    if (nextMode === mode) return;
    const data = await patchConfig({ mode: nextMode }, `Switched to ${nextMode.toUpperCase()} mode`);
    if (data) setMode(data.mode);
  };

  const runTestPayment = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/payments/razorpay-test");
      toast.success(`Test order created: ${data.razorpay_order_id}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  if (!cfg) return <div className="p-10">Loading…</div>;

  const testConfigured = !!(cfg.test_key_id && cfg.test_key_secret_set);
  const liveConfigured = !!(cfg.live_key_id && cfg.live_key_secret_set);
  const webhookConfigured = !!cfg.webhook_secret_set;
  const isConnected = cfg.connection_status === "connected";
  const testPassed = !!cfg.last_test_success_at;

  return (
    <div className="mx-auto max-w-5xl p-6 sm:p-10" data-testid="rzp-settings-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Payments · Razorpay</p>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight">Setup Wizard</h1>
          <p className="mt-2 text-sm text-black/60">A non-technical wizard for connecting your Razorpay account safely. Secrets are stored server-side only.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill ok={isConnected} label={isConnected ? "Connected" : "Not Connected"} testid="pill-connected" />
          <StatusPill ok={mode === "live"} label={mode === "live" ? "LIVE" : "TEST"} testid="pill-mode" />
          <StatusPill ok={testConfigured} label="Test Key" testid="pill-test-key" />
          <StatusPill ok={liveConfigured} label="Live Key" testid="pill-live-key" />
          <StatusPill ok={webhookConfigured} label="Webhook" testid="pill-webhook" />
          <StatusPill ok={testPassed} label="Test Passed" testid="pill-test-passed" />
        </div>
      </div>

      {/* Progress checklist */}
      <div className="mt-8 rounded-lg border border-black/10 bg-white p-5" data-testid="checklist">
        <p className="mb-3 font-mono text-[11px] uppercase tracking-overline text-black/50">Progress Checklist</p>
        <ul className="grid gap-2 sm:grid-cols-2">
          {[
            ["Razorpay account created", true],
            ["Business info saved", !!business.legal_name],
            ["Test API Key configured", testConfigured],
            ["Webhook secret configured", webhookConfigured],
            ["Test payment ran successfully", testPassed],
            ["Live API Key configured", liveConfigured],
            ["Live Mode enabled", mode === "live"],
          ].map(([label, ok]) => (
            <li key={label} className="flex items-center gap-2 text-sm">
              {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-black/25" />}
              <span className={ok ? "text-black" : "text-black/50"}>{label}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Mode toggle */}
      <div className="mt-6 rounded-lg border border-black/10 bg-white p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-overline text-black/50">Payment Mode</p>
            <p className="mt-1 text-sm text-black/70">Test Mode uses fake money. Live Mode charges real customers. Live requires a successful test payment first.</p>
          </div>
          <div className="flex items-center gap-2 rounded-full bg-black/5 p-1" data-testid="mode-toggle">
            <button onClick={() => changeMode("test")} className={`rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-overline transition-colors ${mode === "test" ? "bg-white text-black shadow-sm" : "text-black/50"}`} data-testid="mode-test">Test</button>
            <button onClick={() => changeMode("live")} className={`rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-overline transition-colors ${mode === "live" ? "bg-[#0A2540] text-white shadow-sm" : "text-black/50"}`} data-testid="mode-live">Live</button>
          </div>
        </div>
      </div>

      {/* A. BUSINESS INFO */}
      <section className="mt-6 rounded-lg border border-black/10 bg-white p-6" data-testid="section-business">
        <div className="mb-5 flex items-center gap-3">
          <Building2 className="h-5 w-5 text-[#2F80ED]" />
          <h2 className="font-display text-lg font-bold tracking-tight">A · Business Information</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Legal Business Name" hint="Exact name on your PAN / incorporation certificate" testid="f-legal">
            <input className={inputCls} value={business.legal_name} onChange={(e) => setBusiness({ ...business, legal_name: e.target.value })} placeholder="GD Global Health Ltd" data-testid="in-legal" />
          </Field>
          <Field label="Brand Name" hint="What customers see (e.g. Physio Essential)" testid="f-brand">
            <input className={inputCls} value={business.brand_name} onChange={(e) => setBusiness({ ...business, brand_name: e.target.value })} placeholder="Physio Essential" data-testid="in-brand" />
          </Field>
          <Field label="Business Type" testid="f-type">
            <select className={inputCls} value={business.business_type} onChange={(e) => setBusiness({ ...business, business_type: e.target.value })} data-testid="in-type">
              <option value="">Select…</option>
              {BUSINESS_TYPES.map((b) => <option key={b.v} value={b.v}>{b.l}</option>)}
            </select>
          </Field>
          <Field label="Business PAN" hint="Personal PAN if Proprietorship" testid="f-pan">
            <input className={inputCls} value={business.pan} onChange={(e) => setBusiness({ ...business, pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" maxLength={10} data-testid="in-pan" />
          </Field>
          <Field label="GSTIN" optional testid="f-gstin">
            <input className={inputCls} value={business.gstin} onChange={(e) => setBusiness({ ...business, gstin: e.target.value.toUpperCase() })} placeholder="22ABCDE1234F1Z5" maxLength={15} data-testid="in-gstin" />
          </Field>
          <Field label="Business Email" testid="f-email">
            <input className={inputCls} type="email" value={business.email} onChange={(e) => setBusiness({ ...business, email: e.target.value })} placeholder="physioessential.supply@gmail.com" data-testid="in-email" />
          </Field>
          <Field label="Business Phone" hint="India — will be prefixed with +91" testid="f-phone">
            <input className={inputCls} value={business.phone} onChange={(e) => setBusiness({ ...business, phone: e.target.value })} placeholder="7838687143" data-testid="in-phone" />
          </Field>
          <Field label="Registered Address" testid="f-address">
            <input className={inputCls} value={business.address} onChange={(e) => setBusiness({ ...business, address: e.target.value })} placeholder="Address line" data-testid="in-address" />
          </Field>
        </div>
        <button onClick={saveBusiness} disabled={saving} className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#0A2540] px-5 py-2.5 font-mono text-xs uppercase tracking-overline text-white hover:bg-[#2F80ED] disabled:opacity-60" data-testid="save-business">
          <Save className="h-3.5 w-3.5" /> Save Business Info
        </button>
      </section>

      {/* B. API KEYS */}
      <section className="mt-6 rounded-lg border border-black/10 bg-white p-6" data-testid="section-keys">
        <div className="mb-5 flex items-center gap-3">
          <Key className="h-5 w-5 text-[#2F80ED]" />
          <h2 className="font-display text-lg font-bold tracking-tight">B · Razorpay API Keys</h2>
        </div>

        <div className="mb-5 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">Secrets stay server-side.</p>
            <p className="mt-1 leading-relaxed">Your Razorpay Key Secret is stored on the server only. It is never sent back to your browser, never logged, and never exposed in the frontend code.</p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* TEST KEYS */}
          <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-5">
            <p className="mb-3 font-mono text-[11px] uppercase tracking-overline text-emerald-700">Test Mode Keys · Safe to test</p>
            <div className="space-y-3">
              <Field label="Test Key ID" hint="Starts with rzp_test_">
                <input className={inputCls} value={keys.test_key_id} onChange={(e) => setKeys({ ...keys, test_key_id: e.target.value })} placeholder="rzp_test_XXXXXXXXXX" data-testid="in-test-key-id" />
              </Field>
              <Field label="Test Key Secret" hint={cfg.test_key_secret_set ? `Currently set — ${cfg.test_key_secret_masked} (leave blank to keep)` : "Paste your Test Key Secret"}>
                <div className="relative">
                  <input className={inputCls} type={showSecrets.test ? "text" : "password"} value={keys.test_key_secret} onChange={(e) => setKeys({ ...keys, test_key_secret: e.target.value })} placeholder="••••••••••••••••" data-testid="in-test-secret" />
                  <button type="button" onClick={() => setShowSecrets({ ...showSecrets, test: !showSecrets.test })} className="absolute right-2 top-1/2 -translate-y-1/2 font-mono text-[10px] uppercase text-black/40 hover:text-black">{showSecrets.test ? "hide" : "show"}</button>
                </div>
              </Field>
            </div>
          </div>

          {/* LIVE KEYS */}
          <div className="rounded-md border border-red-200 bg-red-50/40 p-5">
            <p className="mb-3 font-mono text-[11px] uppercase tracking-overline text-red-700">Live Mode Keys · Real money</p>
            <div className="space-y-3">
              <Field label="Live Key ID" hint="Starts with rzp_live_ (only after KYC approval)">
                <input className={inputCls} value={keys.live_key_id} onChange={(e) => setKeys({ ...keys, live_key_id: e.target.value })} placeholder="rzp_live_XXXXXXXXXX" data-testid="in-live-key-id" />
              </Field>
              <Field label="Live Key Secret" hint={cfg.live_key_secret_set ? `Currently set — ${cfg.live_key_secret_masked} (leave blank to keep)` : "Paste your Live Key Secret"}>
                <div className="relative">
                  <input className={inputCls} type={showSecrets.live ? "text" : "password"} value={keys.live_key_secret} onChange={(e) => setKeys({ ...keys, live_key_secret: e.target.value })} placeholder="••••••••••••••••" data-testid="in-live-secret" />
                  <button type="button" onClick={() => setShowSecrets({ ...showSecrets, live: !showSecrets.live })} className="absolute right-2 top-1/2 -translate-y-1/2 font-mono text-[10px] uppercase text-black/40 hover:text-black">{showSecrets.live ? "hide" : "show"}</button>
                </div>
              </Field>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <Field label="Webhook Secret" hint="In Razorpay Dashboard → Settings → Webhooks, create a webhook pointing to the URL below and set a secret. Paste the same secret here.">
            <div className="relative">
              <input className={inputCls} type={showSecrets.webhook ? "text" : "password"} value={keys.webhook_secret} onChange={(e) => setKeys({ ...keys, webhook_secret: e.target.value })} placeholder={cfg.webhook_secret_set ? cfg.webhook_secret_masked : "Paste webhook secret"} data-testid="in-webhook-secret" />
              <button type="button" onClick={() => setShowSecrets({ ...showSecrets, webhook: !showSecrets.webhook })} className="absolute right-2 top-1/2 -translate-y-1/2 font-mono text-[10px] uppercase text-black/40 hover:text-black">{showSecrets.webhook ? "hide" : "show"}</button>
            </div>
          </Field>
          <div className="mt-3 rounded-md bg-black/5 p-3 font-mono text-[11px] text-black/70" data-testid="webhook-url">
            <span className="text-black/40">WEBHOOK URL: </span>
            <span className="break-all">{window.location.origin}/api/razorpay/webhook</span>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={saveKeys} disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-[#0A2540] px-5 py-2.5 font-mono text-xs uppercase tracking-overline text-white hover:bg-[#2F80ED] disabled:opacity-60" data-testid="save-keys">
            <Save className="h-3.5 w-3.5" /> Save API Keys
          </button>
          <button onClick={runTestPayment} disabled={testing || !testConfigured} className="inline-flex items-center gap-2 rounded-md border border-emerald-600 bg-white px-5 py-2.5 font-mono text-xs uppercase tracking-overline text-emerald-700 hover:bg-emerald-50 disabled:opacity-50" data-testid="run-test">
            <PlayCircle className="h-3.5 w-3.5" /> {testing ? "Testing…" : "Run Test Payment"}
          </button>
        </div>
      </section>

      {/* C. Bank/Settlement info block */}
      <section className="mt-6 rounded-lg border border-black/10 bg-white p-6" data-testid="section-bank">
        <div className="mb-3 flex items-center gap-3">
          <PlugZap className="h-5 w-5 text-[#2F80ED]" />
          <h2 className="font-display text-lg font-bold tracking-tight">C · Bank &amp; Settlements</h2>
        </div>
        <p className="text-sm text-black/60">
          Bank account and settlement schedule are configured directly on the Razorpay dashboard —
          Razorpay handles the money movement. Nothing sensitive is stored here.
        </p>
        <a href="https://dashboard.razorpay.com/app/bank-accounts" target="_blank" rel="noreferrer"
          className="mt-4 inline-flex items-center gap-2 rounded-md border border-black/15 bg-white px-4 py-2 font-mono text-[11px] uppercase tracking-overline hover:bg-black hover:text-white">
          Open Razorpay Bank Settings <ExternalLink className="h-3 w-3" />
        </a>
      </section>

      {/* D. WEBSITE INFO */}
      <section className="mt-6 rounded-lg border border-black/10 bg-white p-6" data-testid="section-website">
        <div className="mb-5 flex items-center gap-3">
          <Globe className="h-5 w-5 text-[#2F80ED]" />
          <h2 className="font-display text-lg font-bold tracking-tight">D · Website Information</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Website URL"><input className={inputCls} value={website.website_url} onChange={(e) => setWebsite({ ...website, website_url: e.target.value })} placeholder="https://..." data-testid="in-web-url" /></Field>
          <Field label="Support Email"><input className={inputCls} value={website.support_email} onChange={(e) => setWebsite({ ...website, support_email: e.target.value })} placeholder="support@..." data-testid="in-support-email" /></Field>
          <Field label="Support Phone" optional><input className={inputCls} value={website.support_phone} onChange={(e) => setWebsite({ ...website, support_phone: e.target.value })} placeholder="+91 ..." data-testid="in-support-phone" /></Field>
        </div>
        <button onClick={saveWebsite} disabled={saving} className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#0A2540] px-5 py-2.5 font-mono text-xs uppercase tracking-overline text-white hover:bg-[#2F80ED] disabled:opacity-60" data-testid="save-website">
          <Save className="h-3.5 w-3.5" /> Save Website Info
        </button>
      </section>

      {/* Setup Guide */}
      <section className="mt-6 rounded-lg border border-black/10 bg-[#F9F9F6] p-6" data-testid="section-guide">
        <div className="mb-4 flex items-center gap-3">
          <Info className="h-5 w-5 text-[#2F80ED]" />
          <h2 className="font-display text-lg font-bold tracking-tight">Setup Guide — What to do at Razorpay</h2>
        </div>
        <ol className="space-y-3 text-sm text-black/75">
          <li><strong>1.</strong> Sign in to <a href="https://dashboard.razorpay.com" target="_blank" rel="noreferrer" className="text-[#2F80ED] underline">dashboard.razorpay.com</a>.</li>
          <li><strong>2.</strong> Complete <em>Business KYC</em> (documents vary by business type). Test API keys work even before KYC is approved.</li>
          <li><strong>3.</strong> Go to <em>Account &amp; Settings → API Keys → Generate Test Key</em>. Copy the Key ID + Key Secret and paste into Section B above.</li>
          <li><strong>4.</strong> Click <em>Save API Keys</em>, then <em>Run Test Payment</em>. A green success toast means the keys work.</li>
          <li><strong>5.</strong> In Razorpay Dashboard → <em>Settings → Webhooks</em>, add a new webhook with:
            <ul className="mt-1 list-disc pl-6 text-black/60">
              <li>URL: <code className="rounded bg-black/5 px-1">{window.location.origin}/api/razorpay/webhook</code></li>
              <li>Secret: any strong random string (paste the same into Section B above)</li>
              <li>Events: <code>payment.captured</code>, <code>payment.failed</code>, <code>refund.created</code>, <code>refund.processed</code></li>
            </ul>
          </li>
          <li><strong>6.</strong> Once KYC is approved, come back here, generate LIVE keys on Razorpay, paste them, click <em>Save API Keys</em>, then switch the toggle to <strong>Live</strong>.</li>
        </ol>
      </section>

      <div className="mt-8 rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>Nothing on this page is publicly visible. Only whitelisted admins (via Google Auth) can view or change these settings.</span>
      </div>
    </div>
  );
}
